/**
 * BOXX Print Bridge — product entry point (vendor this whole folder).
 * Canonical source: c:/ManageBOXX/_shared_print_bridge/client/index.ts
 *
 * Re-exports the transport client + React glue, and adds the two helpers a
 * product wires in. Both are ADDITIVE: the existing Print/PDF behaviour is the
 * fallback, so nothing changes when the bridge isn't installed.
 *
 *   printHtmlOrWindow(html, opts)   — for screens that BUILD an HTML doc string
 *                                     (invoices, receipts, labels, reports).
 *   printPageOrFallback(opts)       — for screens that print the LIVE page via
 *                                     `@media print` + window.print(). Captures
 *                                     the page (styles inlined, @media print
 *                                     preserved) and silent-prints it; falls
 *                                     back to window.print().
 *
 * Keep the existing default Print button as-is; add a SEPARATE "Print direct"
 * action that calls one of these, especially for thermal/label/specialised jobs.
 */
export * from './printBridge'
export { usePrintBridge } from './usePrintBridge'
export { PrinterPicker, default as PrinterPickerDefault } from './PrinterPicker'

import { detectBridge, listPrinters, printHtml, type PrintGeometry } from './printBridge'

function rememberedPrinter(rememberKey?: string): string {
  if (!rememberKey) return ''
  try { return localStorage.getItem(`boxx.printBridge.printer.${rememberKey}`) || '' } catch { return '' }
}

async function resolvePrinter(printerName: string | null | undefined, rememberKey?: string): Promise<string> {
  let target = (printerName || rememberedPrinter(rememberKey) || '').trim()
  if (!target) { try { target = (await listPrinters()).default || '' } catch { /* none */ } }
  return target
}

export interface PrintHtmlOrWindowOpts extends PrintGeometry {
  rememberKey?: string
  printerName?: string | null
  title?: string
  fallback?: () => void
  onPrinted?: (pages?: number) => void
  onFallback?: (reason: string) => void
}

/** Print an HTML doc string: bridge-first (silent), else the classic print window. */
export async function printHtmlOrWindow(html: string, opts: PrintHtmlOrWindowOpts = {}): Promise<'bridge' | 'window'> {
  const { rememberKey, printerName, title, fallback, onPrinted, onFallback, ...geo } = opts
  const openWindow = () => {
    if (fallback) { fallback(); return }
    const w = window.open('', '_blank', 'width=820,height=1100')
    if (!w) { onFallback?.('popup blocked'); return }
    w.document.write(html); w.document.close(); w.focus()
    setTimeout(() => { try { w.print() } catch { /* manual */ } }, 250)
  }
  let available = false
  try { available = await detectBridge() } catch { available = false }
  if (!available) { onFallback?.('bridge not installed'); openWindow(); return 'window' }
  const target = await resolvePrinter(printerName, rememberKey)
  if (!target) { onFallback?.('no printer'); openWindow(); return 'window' }
  try {
    const res = await printHtml(html, target, geo)
    if (res.ok) { onPrinted?.(res.pages); return 'bridge' }
    onFallback?.(res.error || 'print failed'); openWindow(); return 'window'
  } catch (e: any) { onFallback?.(e?.message || 'bridge error'); openWindow(); return 'window' }
}

/**
 * Serialise the current document (or a target element) into a standalone HTML
 * string the bridge can print. Inlines same-origin stylesheet rules (so Tailwind
 * + @media print survive) and adds a <base> so relative URLs resolve. WebView2
 * applies @media print when it prints, so output matches window.print().
 */
export function captureDocumentHtml(target?: HTMLElement | null): string {
  let cssText = ''
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = (sheet as CSSStyleSheet).cssRules
      if (!rules) continue
      for (const r of Array.from(rules)) cssText += r.cssText + '\n'
    } catch { /* cross-origin sheet — its <link> is carried in <head> below */ }
  }
  const headLinks = Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))
    .map((n) => n.outerHTML).join('\n')
  const body = (target || document.body).outerHTML
  const base = `<base href="${location.origin}/">`
  return `<!doctype html><html><head><meta charset="utf-8">${base}${headLinks}<style>${cssText}</style></head><body>${body}</body></html>`
}

/**
 * UNIVERSAL hook — call ONCE per app (in main.tsx / index.tsx). Overrides
 * window.print() so EVERY existing print in the product routes to the chosen
 * Windows printer via the agent, with NO per-call-site changes. When the agent
 * isn't installed it calls the original window.print() — so behaviour is
 * unchanged for everyone without the bridge.
 *
 *   import { installPrintBridge } from '@/lib/printBridge'
 *   installPrintBridge({ rememberKey: 'default' })
 *
 * Specialised jobs (labels at exact mm, raw ZPL/TSPL, thermal receipt width)
 * should still call printHtmlOrWindow/printRawBase64 directly so the agent gets
 * the right geometry — those take precedence over this page-capture path.
 */
let _installed = false
export function installPrintBridge(opts: { rememberKey?: string; geometry?: PrintGeometry } = {}): void {
  if (_installed || typeof window === 'undefined') return
  _installed = true
  const native = window.print.bind(window)
  // Do NOT eagerly probe the agent here. A warm-up connect attempts
  // ws://localhost:8767 on every page load (incl. the login screen), and the
  // browser logs a red "WebSocket connection failed" whenever no agent is
  // installed — which is the common case. Detect lazily, only when a print is
  // actually requested (below), so idle / unauthenticated pages stay quiet.
  window.print = function patchedPrint() {
    // Capture synchronously (DOM + @media print state) before any await.
    let html = ''
    try { html = captureDocumentHtml() } catch { native(); return }
    ;(async () => {
      try {
        if (await detectBridge()) {
          const printer = await resolvePrinter(undefined, opts.rememberKey)
          if (printer) {
            const r = await printHtml(html, printer, opts.geometry || {})
            if (r.ok) return
          }
        }
      } catch { /* fall through to native */ }
      native()
    })()
  } as typeof window.print
}

export interface PrintPageOpts extends PrintGeometry {
  rememberKey?: string
  printerName?: string | null
  /** Element to print; defaults to document.body (with @media print rules applied). */
  target?: HTMLElement | null
  onPrinted?: (pages?: number) => void
  onFallback?: (reason: string) => void
}

/**
 * Direct-print the current page via the bridge; falls back to window.print().
 * Use for screens that print the live page through `@media print`.
 */
export async function printPageOrFallback(opts: PrintPageOpts = {}): Promise<'bridge' | 'window'> {
  const { rememberKey, printerName, target, onPrinted, onFallback, ...geo } = opts
  let available = false
  try { available = await detectBridge() } catch { available = false }
  if (!available) { onFallback?.('bridge not installed'); window.print(); return 'window' }
  const printer = await resolvePrinter(printerName, rememberKey)
  if (!printer) { onFallback?.('no printer'); window.print(); return 'window' }
  try {
    const res = await printHtml(captureDocumentHtml(target), printer, geo)
    if (res.ok) { onPrinted?.(res.pages); return 'bridge' }
    onFallback?.(res.error || 'print failed'); window.print(); return 'window'
  } catch (e: any) { onFallback?.(e?.message || 'bridge error'); window.print(); return 'window' }
}
